'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface LocalPartner {
  id: string;
  name: string;
  type: string;
  country: string;
  services: string[];
  contact: string;
  image: string;
  alt: string;
  certifications: string[];
}

interface LocalPartnerDirectoryProps {
  partners: LocalPartner[];
}

const LocalPartnerDirectory = ({ partners }: LocalPartnerDirectoryProps) => {
  const [selectedType, setSelectedType] = useState<string>('all');

  const partnerTypes = ['all', 'customs', 'security', 'warehouse', 'transport'];

  const filteredPartners =
    selectedType === 'all'
      ? partners
      : partners.filter((p) => p.type.toLowerCase() === selectedType);

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6">
      <div className="mb-6">
        <h3 className="font-headline font-bold text-xl text-foreground mb-2">
          Local Partner Network
        </h3>
        <p className="font-body text-sm text-muted-foreground">
          Trusted partners ensuring seamless logistics across Africa
        </p>
      </div>

      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {partnerTypes.map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`px-4 py-2 rounded-lg font-body text-sm font-semibold transition-all duration-300 ${
                selectedType === type
                  ? 'bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark text-primary shadow-golden-md'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredPartners.map((partner) => (
          <div
            key={partner.id}
            className="p-4 bg-background border border-border rounded-lg hover:shadow-elevation-1 transition-all duration-300"
          >
            <div className="flex items-start space-x-4 mb-3">
              <div className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                <AppImage
                  src={partner.image}
                  alt={partner.alt}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h4 className="font-headline font-bold text-base text-foreground mb-1">
                  {partner.name}
                </h4>
                <div className="flex items-center space-x-2 text-xs font-body text-muted-foreground">
                  <span className="px-2 py-1 bg-golden-light/10 text-golden-mid rounded">
                    {partner.type}
                  </span>
                  <span className="flex items-center space-x-1">
                    <Icon name="MapPinIcon" size={12} />
                    <span>{partner.country}</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <p className="text-xs font-body text-muted-foreground mb-2">
                Services:
              </p>
              <div className="flex flex-wrap gap-1">
                {partner.services.map((service, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-muted text-xs font-body text-foreground rounded"
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="flex items-center space-x-1">
                {partner.certifications.map((cert, idx) => (
                  <Icon
                    key={idx}
                    name="ShieldCheckIcon"
                    size={16}
                    className="text-success"
                  />
                ))}
              </div>
              <button className="flex items-center space-x-1 text-xs font-body font-semibold text-primary hover:text-golden-mid transition-colors duration-300">
                <Icon name="PhoneIcon" size={14} />
                <span>Contact</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LocalPartnerDirectory;